import { Div, Label } from '@kizi/core-components';
import React from 'react';
import { ComparisonLabel } from '../../../src/components/comparison/ComparisonLabel';
import { carHelpers } from './car/helpers';
import { cbHealthHelpers } from './cb-health/helpers';
import { energyHelpers } from './energy/helpers';
import { homeHelpers } from './home/helpers';
import { osHealthHelpers } from './os-health/helpers';
import { oshomeHelpers } from './os-home/helpers';
import { osTravelHelpers } from './os-travel/helpers';
import { osTriplePlayHelpers } from './os-triple-play/helpers';
import { testHelpers } from './test/helpers';
import { travelHelpers } from './travel/helpers';
import { triplePlayHelpers } from './triple-play/helpers';

interface LabelObject {
  label: string;
  children?: string[];
  [key: string]: any;
}

interface ProductData {
  value?: any;
  content?: React.ReactNode;
  isVisible?: boolean;
  label?: string;
  children?: React.ReactNode[];
  childrenProps?: any[];
}

interface AccordionRow {
  labels: React.ReactNode[];
  rows: React.ReactNode[][];
}

const COMPARATOR_HELPERS = {
  energy: energyHelpers,
  car: carHelpers,
  home: homeHelpers,
  'os-home': oshomeHelpers,
  tripleplay: triplePlayHelpers,
  'cb-health': cbHealthHelpers,
  'os-health': osHealthHelpers,
  travel: travelHelpers,
  'os-travel': osTravelHelpers,
  'os-triple-play': osTriplePlayHelpers,
  test: testHelpers,
} as const;

const HIGHLIGHT_BACKGROUND = '#FFA61733';
const DEFAULT_BACKGROUND = 'inherit';

const hasValueDifferences = (values: any[]): boolean => {
  if (values.length === 0) return false;

  const firstValue = values[0];
  if (firstValue === undefined || firstValue === null) return false;

  return values.some((value) => value !== firstValue);
};

const createTableCell = (
  product: ProductData,
  index: number,
  shouldHighlight: boolean,
): React.ReactNode => {
  if (!product?.isVisible) {
    return <Div key={`div-${index}`} />;
  }

  return (
    <Div
      key={index}
      background={shouldHighlight ? HIGHLIGHT_BACKGROUND : DEFAULT_BACKGROUND}
    >
      {product.content}
    </Div>
  );
};

const createStandardRow = (
  labelObject: LabelObject,
  productsDataColumn: ProductData[][],
  index: number,
): React.ReactNode[] => {
  const rowValues = productsDataColumn.map((product) => product[index]?.value);
  const shouldHighlight = hasValueDifferences(rowValues);

  return [
    <ComparisonLabel key={`label-${index}`} {...labelObject} />,
    ...productsDataColumn.map((product) =>
      createTableCell(product[index], index, shouldHighlight),
    ),
  ];
};

const filterEmptyRows = (rows: any[]): any[] => {
  const filteredRows = [...rows];

  const emptyRowIndices = rows
    .map((row, index) => {
      if (!Array.isArray(row)) return null;

      const filteredContent = row
        .map((cell) => cell.props?.children)
        .filter(Boolean);

      return filteredContent.length === 0 ? index : null;
    })
    .filter((index): index is number => index !== null)
    .reverse();

  emptyRowIndices.forEach((index) => {
    filteredRows.splice(index, 1);
  });

  return filteredRows;
};

export const structureDataTableRow = (
  labels: LabelObject[],
  productsDataColumn: ProductData[][],
): any[] => {
  const rows = labels.map((labelObject, index) => {
    if ('children' in labelObject && labelObject.children) {
      return structureAccordionRow(
        labelObject,
        productsDataColumn.map((product) => product[index]),
      );
    }

    return createStandardRow(labelObject, productsDataColumn, index);
  });

  return filterEmptyRows(rows);
};

export const structureAccordionRow = (
  labelObject: LabelObject,
  products: ProductData[],
): AccordionRow | React.ReactNode => {
  if (products.some((product) => !product)) {
    return <Div />;
  }

  const { children = [] } = labelObject;

  return {
    labels: [
      <ComparisonLabel key="accordion-label" {...labelObject} />,
      ...products.map((product, index) => (
        <Div key={`product-label-${index}`}>{product.label}</Div>
      )),
    ],
    rows: children.map((child, childIndex) => [
      <Label key={`child-label-${childIndex}`} fontWeight="initial" id={child}>
        {child}
      </Label>,
      ...products.map((product, productIndex) => (
        <Div
          key={`child-content-${productIndex}`}
          {...product?.childrenProps?.[childIndex]}
        >
          {product.children?.[childIndex]}
        </Div>
      )),
    ]),
  };
};

export const getComparatorHandler = (
  comparator: string,
  section: string,
): any => {
  const helpers =
    COMPARATOR_HELPERS[comparator as keyof typeof COMPARATOR_HELPERS];

  if (!helpers) {
    console.warn(`Unknown comparator: ${comparator}`, { comparator, section });
    return undefined;
  }

  const handler = helpers[section];

  if (!handler) {
    console.warn(`Unknown section for ${comparator}: ${section}`, {
      comparator,
      section,
    });
  }

  return handler;
};
